/**
 * @fileoverview Componente Molecule para visualización del estado y metadatos de mensajes en la bandeja de entrada
 * @module MessageStatusMC
 * @category Component - Molecule
 * @since 1.0.0
 * @version 3.1.0
 */

import React, { memo, useMemo } from "react";
import { useTranslation } from "react-i18next";
import MessageDateTimeMC from "./MessageDateTimeMC";
import MessageStatusIconMC from "./MessageStatusIconMC";
import IconMC from "../../../components/IconMC";

/**
 * @typedef {Object} MessageData
 * @description Estructura de datos del mensaje para mostrar su estado y metadatos
 * @property {boolean} [isNote] - Indica si es una nota interna
 * @property {boolean} [isShowIcon] - Indica si debe mostrar el icono de estado
 * @property {boolean} [showError] - Indica si debe mostrar mensaje de error
 * @property {string} [errorMessage] - Mensaje de error específico
 * @property {boolean} [isSentByMia] - Indica si fue enviado por el chatbot Mia
 * @property {string} [senderFullName] - Nombre completo del remitente
 * @property {Date} createdAt - Fecha de creación del mensaje (requerido por MessageDateTimeMC)
 */

/**
 * Componente Molecule para visualización del estado y metadatos de mensajes.
 * 
 * Maneja la presentación de información contextual del mensaje incluyendo timestamp,
 * estado de entrega, errores, información del remitente y indicadores especiales
 * como mensajes enviados por chatbot.
 * 
 * @component MessageStatusMC
 * @category Component - Molecule
 * 
 * @description
 * **Características:**
 * - Visualización de fecha y hora del mensaje (MessageDateTimeMC)
 * - Iconos de estado de entrega según plataforma (MessageStatusIconMC)
 * - Manejo de mensajes de error con estilos específicos
 * - Indicador especial para mensajes del chatbot Mia
 * - Información del remitente con texto contextual
 * - Layout adaptable para medios y mensajes de texto
 * 
 * **Atomic Design:**
 * - **Nivel**: Molecule
 * - **Composición**: Combina MessageDateTimeMC, MessageStatusIconMC e IconMC
 * - **Dependencias**: MessageDateTimeMC (atom), MessageStatusIconMC (atom), IconMC (atom)
 * - **Contexto**: Reutilizable en diferentes tipos de mensaje
 * 
 * **Props Interface:**
 * ```javascript
 * {
 *   isMedia?: boolean,              // Layout especial para mensajes multimedia
 *   message: MessageData,           // Datos del mensaje con metadatos
 *   platform: string,               // Plataforma para iconos específicos
 *   containerClassName?: string     // Clases CSS adicionales para el contenedor
 * }
 * ```
 * 
 * **Arquitectura Optimizada (v3.1.0):**
 * - ✅ **Molecule Level**: Combina múltiples átomos para funcionalidad específica
 * - ✅ **Conditional Rendering**: Solo muestra elementos cuando son necesarios
 * - ✅ **Performance**: React.memo y useMemo para optimización
 * - ✅ **Responsive Layout**: Adaptación automática para diferentes contextos
 * - ✅ **Internationalization**: Textos completamente internacionalizados
 * - ✅ **Platform Specific**: Adapta comportamiento según plataforma
 * 
 * **Responsabilidades:**
 * - Mostrar fecha y hora del mensaje de forma consistente
 * - Renderizar iconos de estado según plataforma y estado
 * - Visualizar errores con estilos apropiados (texto blanco, posición)
 * - Identificar mensajes del chatbot con icono y texto específico
 * - Mostrar información del remitente con texto contextual
 * - Adaptar layout para mensajes multimedia vs texto
 * 
 * **NO Maneja:**
 * - ❌ Lógica de determinación de estado (recibe datos procesados)
 * - ❌ Formateo de fechas (delegado a MessageDateTimeMC)
 * - ❌ Selección de iconos específicos (delegado a MessageStatusIconMC)
 * - ❌ Validación de datos del mensaje
 * 
 * **Flujo de Renderización:**
 * 1. **Layout Wrapper**: Posicionamiento absoluto para medios o normal
 * 2. **DateTime + Status**: Línea principal con fecha e icono de estado
 * 3. **Error Message**: Mensaje de error si `showError` es true
 * 4. **Bot Indicator**: Indicador de Mia si `isSentByMia` es true
 * 5. **Sender Info**: Información del remitente si `senderFullName` existe
 * 
 * @param {Object} props - Propiedades del componente
 * @param {boolean} [props.isMedia=false] - Indica si es para un mensaje multimedia (cambia layout)
 * @param {MessageData} props.message - Objeto con datos y metadatos del mensaje
 * @param {string} props.platform - Plataforma de origen ('whatsapp'|'messenger'|'instagram')
 * @param {string} [props.containerClassName=''] - Clases CSS adicionales para el contenedor
 * 
 * @returns {JSX.Element} Elemento JSX que renderiza el estado y metadatos del mensaje
 * 
 * @example
 * ```jsx
 * // Uso básico en mensaje de texto
 * <MessageStatusMC 
 *   message={{
 *     createdAt: new Date(),
 *     isShowIcon: true,
 *     senderFullName: "Juan Pérez"
 *   }}
 *   platform="whatsapp"
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Uso en mensaje multimedia con error
 * <MessageStatusMC 
 *   isMedia={true}
 *   message={{
 *     createdAt: new Date(),
 *     showError: true,
 *     errorMessage: "Error al enviar el archivo",
 *     isShowIcon: true
 *   }}
 *   platform="messenger"
 *   containerClassName="tw-text-sm"
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Uso con mensaje del chatbot
 * <MessageStatusMC 
 *   message={{
 *     createdAt: new Date(),
 *     isSentByMia: true,
 *     isShowIcon: true
 *   }}
 *   platform="whatsapp"
 * />
 * ```
 * 
 * @example
 * ```jsx
 * // Uso con nota interna
 * <MessageStatusMC 
 *   message={{
 *     createdAt: new Date(),
 *     isNote: true,
 *     senderFullName: "Agente María",
 *     isShowIcon: false
 *   }}
 *   platform="whatsapp"
 * />
 * ```
 * 
 * @see {@link MessageDateTimeMC} Componente atom para formateo de fecha y hora
 * @see {@link MessageStatusIconMC} Componente atom para iconos de estado por plataforma
 * @see {@link IconMC} Componente atom para iconografía general
 * 
 * @since 1.0.0
 * @version 3.1.0 - Optimización de renders y mejora de layout responsivo
 */
const MessageStatusMC = memo(({ isMedia = false, message, platform, containerClassName = '' }) => {
  /**
   * Hook de internacionalización para acceder a las traducciones.
   * 
   * @memberof MessageStatusMC
   * @private
   */
  const { t } = useTranslation();

  /**
   * Texto contextual del remitente basado en el tipo de mensaje.
   * 
   * Determina si mostrar "creado" para notas internas o "enviado" para mensajes regulares.
   * Se memoiza para optimizar renders cuando las dependencias no cambian.
   * 
   * @memberof MessageStatusMC
   * @private
   * 
   * @type {string} Texto traducido según el contexto del mensaje
   */
  const senderText = useMemo(
    () =>
      message.isNote
        ? t('InboxMC:created')
        : t('InboxMC:sent'),
    [message.isNote, t]
  );

  return (
    <div
      className={`${isMedia ? 'tw-absolute tw-right-1 tw-pr-1 tw-pl-1 tw-bottom-0' : ''}`}
      style={{ textShadow: isMedia ? '0 0 4px rgba(0,0,0,1)' : '' }}
    >
      <div className={`tw-font-normal ${containerClassName}`}>
        <div className="tw-flex tw-items-center tw-w-full tw-justify-end">
          <div className="tw-flex tw-items-center">
            <MessageDateTimeMC message={message} />
            {message.isShowIcon && (
              <MessageStatusIconMC message={message} platform={platform} />
            )}
            {message.showError && (
              <span className="tw-flex tw-items-center tw-ml-2 tw-py-0.5 tw-px-2 tw-rounded-full tw-bg-red-100 tw-text-red-700">
                <IconMC size="sm" name="alert-circle-outline" className="tw-fill-red-700 tw-mr-1" />
                <small className="tw-text-xs tw-align-middle tw-font-normal tw-whitespace-nowrap">
                  {t('InboxMC:errorDetailCard.message_not_delivered')}
                </small>
              </span>
            )}
          </div>
        </div>
        {message.isSentByMia && (
          <div className="tw-flex tw-w-full tw-justify-end tw-items-center tw-mt-0 tw-text-3xs">
            <span className="tw-flex tw-items-center tw-mr-1">{t('chatbot_ai.message_status')}</span>
            <span className="tw-flex tw-items-center"><IconMC size="xs" name="bot" /></span>
          </div>
        )}
        {message.senderFullName && (
          <div className="tw-flex tw-w-full tw-justify-end tw-mt-0 tw-text-3xs">
            <small className="tw-text-xs tw-align-middle tw-font-normal">
              {`${senderText} ${message.senderFullName}`}
            </small>
          </div>
        )}
      </div>
    </div>
  );
});

MessageStatusMC.displayName = "MessageStatusMC";

export default MessageStatusMC;